import React, { useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "primereact/button";

import EntityFormPage from "../../../../components/EntityFormPage/EntityFormPage";
import StationFormFields from "./StationFormFields";
import StationManagersNotification from "./StationManagersNotification";
import { useSubStationInputs } from "./useSubStationInputs";
import { buildStationPayload, toMinutes } from "./stationFormUtils";
import { ToastContext } from "../../../../App";
import {
  useGetStationListQuery,
  useUpdateStationMutation,
  useUploadImageMutation,
} from "../../../../redux/services/foodStation";
import uploadSubStationImageHelper from "../../../../utils/substationImageUploader";
import { getImageUrl } from "../../../../utils/getImageUrl";
import { parseSubStations } from "../../../../utils/stationUtils";
import getSubdomain from "../../../../utils/getSubdomain";

import * as styles from "../Stations.module.css";

const LIST_PATH = "/app/manager/stations";

function StationEditPage() {
  const showToast = useContext(ToastContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: stations, refetch: refetchStations } = useGetStationListQuery(
    getSubdomain(),
  );
  const station = useMemo(
    () => stations?.find((s) => String(s.id) === String(id)),
    [stations, id],
  );

  const { control, handleSubmit, watch, reset } = useForm({
    defaultValues: {
      title: "",
      midnight_autocomplete: true,
      is_multi: false,
      info: "",
      baseline_time: 6,
      restriction: 10,
      order_max_threshold: "",
      order_min_threshold: "",
    },
  });

  const isMulti = watch("is_multi");

  const subStationState = useSubStationInputs();
  const { subStationInputs, setSubStationInputs, setIsMulti } = subStationState;

  useEffect(() => {
    setIsMulti(isMulti);
  }, [isMulti, setIsMulti]);

  useEffect(() => {
    if (!station) return;
    reset({
      title: station.name || "",
      midnight_autocomplete: !!station.midnight_autocomplete,
      is_multi: !!station.is_multi,
      info: station.info || "",
      baseline_time: toMinutes(station.baseline_time, 6),
      restriction: toMinutes(station.order_restriction, 0),
      order_max_threshold: station.order_max_threshold ?? "",
      order_min_threshold: station.order_min_threshold ?? "",
    });

    const rowSubStations = (station.sub_stations || [])
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((subStation) => ({
        id: subStation.id,
        name: subStation.name,
        active: subStation.active === 1 || subStation.active === true,
        image_name: subStation.image_name || null,
        imageFile: null,
        preview: subStation.image_name
          ? getImageUrl(subStation.image_name)
          : null,
      }))
      .filter((s) => s.name);

    setSubStationInputs(
      rowSubStations.length > 0
        ? rowSubStations
        : station.is_multi
          ? [
              { id: null, name: "" },
              { id: null, name: "" },
            ]
          : [{ id: null, name: "" }],
    );
  }, [station, reset, setSubStationInputs]);

  const [
    updateStation,
    {
      isSuccess: isUpdateStationSuccess,
      isError: isUpdateStationError,
      error: updateStationError,
    },
  ] = useUpdateStationMutation();
  const [uploadImage] = useUploadImageMutation();

  useEffect(() => {
    if (isUpdateStationSuccess) {
      showToast("success", "Station", "Station updated!");
      // Upload any pending sub-station images, then navigate.
      (async () => {
        const uploads = subStationInputs
          .filter((s) => s.id && s.imageFile)
          .map((s) =>
            uploadSubStationImageHelper({
              subStationId: s.id,
              file: s.imageFile,
              uploadImage,
              setSubStationInputs,
              refetchStations,
              getImageUrl,
              showToast,
            }),
          );
        if (uploads.length > 0) {
          await Promise.all(uploads);
        }
        setIsSubmitting(false);
        navigate(LIST_PATH);
      })();
    }
  }, [isUpdateStationSuccess]);

  useEffect(() => {
    if (isUpdateStationError) {
      showToast(
        "error",
        updateStationError?.status,
        updateStationError?.data?.message ||
          (updateStationError?.data?.errors && "Incorrect data"),
      );
      setIsSubmitting(false);
    }
  }, [isUpdateStationError]);

  const onSaveEdit = (data) => {
    if (isSubmitting) return;

    const subStations = data.is_multi ? parseSubStations(subStationInputs) : [];
    if (data.is_multi && subStations.length < 2) {
      showToast(
        "error",
        "Sub-stations",
        "For multi station, add at least 2 sub-station names",
      );
      return;
    }

    updateStation({
      id,
      subdomain: getSubdomain(),
      data: buildStationPayload(data, subStations),
    });
    setIsSubmitting(true);
  };

  const title = station?.name
    ? `Edit Food Station — ${station.name}`
    : "Edit Food Station";

  return (
    <EntityFormPage
      title={title}
      backTo={LIST_PATH}
      footer={
        <Button
          label="Save"
          type="submit"
          disabled={isSubmitting}
          className={`p-button-success ${styles.saveBtn}`}
          onClick={handleSubmit(onSaveEdit)}
        />
      }
      afterFooter={id && <StationManagersNotification stationId={id} />}
    >
      <form onSubmit={handleSubmit(onSaveEdit)}>
        <StationFormFields
          control={control}
          isMulti={isMulti}
          {...subStationState}
        />
        <button type="submit" hidden />
      </form>
    </EntityFormPage>
  );
}

export default StationEditPage;
